import { createRouter, createWebHistory, RouterView, type Router } from 'vue-router';
import ResourcesListComponent from '@/components/resources/ResourcesListComponent.vue';
import ResourceSingleComponent from '@/components/resources/ResourceSingleComponent.vue';
import OnlineWorkshopsComponent from '@/components/OnlineWorkshopsComponent.vue';
import MasterclassTheBetterBoysBlueprintComponent from '@/components/MasterclassTheBetterBoysBlueprintComponent.vue';
import RestorativeClassroomPlaybookComponent from '@/components/RestorativeClassroomPlaybookComponent.vue';
import SurveyAnalyserComponent from '@/components/SurveyAnalyserComponent.vue';
import SurveyCreatorComponent from '@/components/SurveyCreatorComponent.vue';
import PrincipalChampionMessageComponent from '@/components/PrincipalChampionMessageComponent.vue';
import AccountComponent from '@/components/AccountComponent.vue';
import LoginComponent from '@/components/auth/LoginComponent.vue';
import ForgotPasswordComponent from '@/components/auth/ForgotPasswordComponent.vue';
import SetPasswordComponent from '@/components/auth/SetPasswordComponent.vue';
import PasswordResetComponent from '@/components/auth/PasswordResetComponent.vue';
import { useResourceStore } from './stores/resourceStore';
import { useUser } from '@/stores/userStore';
import { trackPageView } from '@/services/activityService';
import { isStaleAssetError, reloadForStaleAsset } from '@/services/staleAssetService';

let router: Router;

export const useRouter = () => {
	if (!router) {
		router = createRouter({
			history: createWebHistory(),
			scrollBehavior(to, _from, savedPosition) {
				if (to.name === 'resourceList' && savedPosition) {
					return savedPosition;
				}

				if (to.hash) {
					return new Promise((resolve) => {
						setTimeout(() => resolve({ el: to.hash, behavior: 'smooth' }), 300);
					});
				}

				return { top: 0 };
			},
			routes: [
				{
					path: '/',
					name: 'home',
					redirect: (to) => {
						const user = useUser();

						if (to.query.redirect) {
							return to.query.redirect as string;
						}

						if (user.isInsideOnboarding) {
							return { name: 'onboarding' };
						}

						if (user.canAccessStaffroom) {
							return { name: 'live-masterclasses' };
						}

						if (user.canAccessDashboard) {
							return { name: 'dashboard' };
						}

						if (user.canAccessPartnership) {
							return { name: 'partnership' };
						}

						return { name: 'resourceList' };
					},
				},
				{
					path: '/auth',
					redirect: () => {
						return { name: 'login' };
					},
					children: [
						{
							path: 'login',
							name: 'login',
							component: LoginComponent,
							meta: { title: 'Login', isPublic: true, requiresPublic: true },
						},
						{
							path: 'forgot-password',
							name: 'forgot-password',
							component: ForgotPasswordComponent,
							meta: { title: 'Forgot Password', isPublic: true, requiresPublic: true, forceLogout: true },
						},
						{
							path: 'set-password',
							name: 'set-password',
							component: SetPasswordComponent,
							meta: { title: 'Set Password', isPublic: true, requiresPublic: true, forceLogout: true },
						},
						{
							path: 'reset-password',
							name: 'reset-password',
							component: PasswordResetComponent,
							meta: { title: 'Reset Password', isPublic: true, requiresPublic: true, forceLogout: true },
						},
						{
							path: 'logout',
							name: 'logout',
							component: {
								beforeMount() {
									router.push({ name: 'login' });
								},
								render() {
									return null;
								},
							},
							meta: { title: 'Logout', forceLogout: true, isPublic: true },
						},
					],
				},
				{
					path: '/admin',
					name: 'admin',
					component: () => import('@/components/AdminComponent.vue'),
					meta: { title: 'Admin', requiresAdmin: true },
				},
				{
					// Operations and administrators, as of 11 Oct 2026. Still not the
					// final audience: it is built to answer a principal's question and
					// the backend shapes its answer around the asking school, so the
					// next widening is to the people it was written for. That remains a
					// one-word change here, in the nav, and on the controller — which
					// enforces the same pair, so this gate is real rather than cosmetic.
					path: '/ops/policy-advisor',
					name: 'policy-advisor',
					component: () => import('@/components/PolicyAdvisorComponent.vue'),
					meta: { title: 'Policy Advisor', requiresOps: true },
				},
				{
					// A school's own uploaded policies. **Built for champions and
					// deliberately not open to them yet** — it sits under the same
					// gate as the advisor that reads these documents, so the two
					// widen together in one change rather than leaving a champion
					// able to upload a policy they cannot then ask about.
					//
					// Keep in step with SchoolPolicyController::enforceAudience().
					// That one is the real check; this decides whether someone sees
					// a page that would then fail every call on it.
					// `requiresEfOrMore`, not `requiresOps`, because an Expert
					// Facilitator may see that their school's policies exist and
					// whether they are confirmed — a partnership question —
					// without reading them. The server decides what they get;
					// this only decides who is shown the page.
					path: '/ops/school-policies',
					name: 'school-policies',
					component: () => import('@/components/SchoolPolicyComponent.vue'),
					meta: { title: 'School Policies', requiresEfOrMore: true },
				},
				{
					// The email library: emails written once and pointed at by as
					// many events as want them. One route per thing, the same as
					// the events screens below and for the same reason — an email
					// somebody is writing is something they send a colleague a
					// link to.
					//
					// Operations and administrators, matching
					// EmailLibraryController. Ops write these; the controller
					// gates the endpoints rather than the routes hiding them.
					path: '/ops/emails',
					component: RouterView,
					meta: { title: 'Emails', requiresOps: true },
					children: [
						{
							path: '',
							name: 'email-library',
							component: () => import('@/components/emails/EmailLibraryComponent.vue'),
							meta: { title: 'Emails', requiresOps: true },
						},
						{
							// Not under :id, because there is no email yet.
							path: 'new',
							name: 'email-library-new',
							component: () => import('@/components/emails/EmailLibraryFormComponent.vue'),
							meta: { title: 'New email', requiresOps: true },
						},
						{
							path: ':id',
							name: 'email-library-edit',
							component: () => import('@/components/emails/EmailLibraryFormComponent.vue'),
							meta: { title: 'Email', requiresOps: true },
						},
						{
							path: ':id/sends',
							name: 'email-library-sends',
							component: () => import('@/components/emails/EmailLibrarySendsComponent.vue'),
							meta: { title: 'Who this email has reached', requiresOps: true },
						},
					],
				},
				{
					// Everything about events. One route per thing you can look at,
					// rather than panels opening under the list: a registration list
					// or an email preview is something you send somebody, and state
					// inside a screen has no address to send.
					//
					// Operations and administrators both, matching
					// EventAdminController — ops run the events, and the controller
					// gates the endpoints rather than the routes hiding them.
					path: '/ops/events',
					component: RouterView,
					meta: { title: 'Events', requiresOps: true },
					children: [
						{
							path: '',
							name: 'event-admin',
							component: () => import('@/components/events/admin/EventAdminComponent.vue'),
							meta: { title: 'Events', requiresOps: true },
						},
						{
							// Not under :id, because there is no event yet.
							path: 'new',
							name: 'event-new',
							component: () => import('@/components/events/admin/EventFormComponent.vue'),
							meta: { title: 'New event', requiresOps: true },
						},
						{
							// Fetches the event once and hands it to whichever of its
							// children is showing.
							path: ':id',
							component: () => import('@/components/events/admin/EventDetailComponent.vue'),
							meta: { requiresOps: true },
							children: [
								{
									path: '',
									redirect: (to) => ({ name: 'event-registrations', params: to.params }),
								},
								{
									path: 'edit',
									name: 'event-edit',
									component: () => import('@/components/events/admin/EventFormComponent.vue'),
									meta: { title: 'Edit event', requiresOps: true },
								},
								{
									path: 'orders',
									name: 'event-orders',
									component: () => import('@/components/events/admin/EventOrdersComponent.vue'),
									meta: { title: 'Orders', requiresOps: true },
								},
								{
									path: 'registrations',
									name: 'event-registrations',
									component: () => import('@/components/events/admin/EventRegistrationsComponent.vue'),
									meta: { title: 'Registrations', requiresOps: true },
								},
								{
									// Keyed on the registration, not the user: a paid
									// event's attendee is an external contact with no
									// portal account and no user id to key on.
									path: 'registrations/:registrationId/emails',
									name: 'event-registration-emails',
									component: () => import('@/components/events/admin/EventRegistrationSendsComponent.vue'),
									meta: { title: 'What was sent', requiresOps: true },
								},
								{
									path: 'emails',
									name: 'event-emails',
									component: () => import('@/components/events/admin/EventEmailsComponent.vue'),
									meta: { title: 'Emails', requiresOps: true },
								},
								{
									path: 'emails/:emailId/sent',
									name: 'event-email-sends',
									component: () => import('@/components/events/admin/EventEmailSendsComponent.vue'),
									meta: { title: 'Who this reached', requiresOps: true },
								},
								{
									// The link you send somebody to show them an email.
									path: 'emails/:emailId/preview',
									name: 'event-email-preview',
									component: () => import('@/components/events/admin/EventEmailPreviewComponent.vue'),
									meta: { title: 'Email preview', requiresOps: true },
								},
							],
						},
					],
				},
				{
					// The rest of the path is the LMS page in the frame, e.g. /learning/courses/x/ is
					// /courses/x/ on the LMS. LearningComponent keeps the two in step.
					path: '/learning/:lmsPath(.*)*',
					name: 'learning',
					component: () => import('@/components/LearningComponent.vue'),
					meta: { title: 'Learning', requiresLearningAccess: true },
				},
				{
					// Named for the group the menu shows, "Expert Facilitator", rather than
					// for resources — two of the four pages under it are tools and a
					// dashboard, not documents. The guard sits on the parent, so a page
					// added here is gated by being here.
					path: '/ef',
					component: RouterView,
					meta: { requiresEfOrMore: true },
					children: [
						{
							path: '',
							name: 'ef-resources',
							component: () => import('@/components/EfResourcesComponent.vue'),
							meta: { title: 'EF Resources' },
						},
						{
							// Under /ef rather than the staffroom because it is about
							// every school rather than the one you have selected — and
							// Simon, who runs these sessions, holds an EF role.
							path: 'vpa-bookings',
							name: 'vpa-bookings',
							component: () => import('@/components/VpaBookingsComponent.vue'),
							meta: { title: 'VPA 2027 bookings' },
						},
						{
							path: 'survey-creator',
							name: 'survey-creator',
							component: SurveyCreatorComponent,
							meta: { title: 'Survey Creator' },
						},
						{
							path: 'survey-analyser',
							name: 'survey-analyser',
							component: SurveyAnalyserComponent,
							meta: { title: 'Survey Analyser' },
						},
						{
							path: 'event-bookings',
							name: 'event-bookings',
							component: () => import('@/components/EventBookingsComponent.vue'),
							meta: { title: 'Event Bookings' },
						},
					],
				},
				{
					path: '/ops/activity',
					name: 'activity',
					component: () => import('@/components/ActivityComponent.vue'),
					meta: { title: 'Activity', requiresEfOrMore: true },
				},
				{
					// Sending an ordinary email to portal staff. **Not under /ops**,
					// which is administrators and operations only: EFs send to their
					// own schools, so this is `requiresEfOrMore` and sits at the top
					// level. The audiences an EF may not use are refused by the
					// backend's resolver rather than by this guard.
					path: '/emails',
					component: RouterView,
					meta: { requiresEfOrMore: true },
					children: [
						{
							path: '',
							name: 'broadcast-list',
							component: () => import('@/components/broadcasts/BroadcastListComponent.vue'),
							meta: { title: 'Emails sent', requiresEfOrMore: true },
						},
						{
							// Not under `:id`, because there is no broadcast yet.
							path: 'send',
							name: 'broadcast-compose',
							component: () => import('@/components/broadcasts/BroadcastComposeComponent.vue'),
							meta: { title: 'Send an email', requiresEfOrMore: true },
						},
						{
							path: ':id',
							name: 'broadcast-detail',
							component: () => import('@/components/broadcasts/BroadcastDetailComponent.vue'),
							meta: { title: 'Who this reached', requiresEfOrMore: true },
						},
					],
				},
				{
					path: '/schools',
					component: RouterView,
					children: [
						{
							path: '',
							name: 'schoolList',
							component: () => import('@/components/school/SchoolListComponent.vue'),
							meta: { title: 'Schools', requiresEfOrMore: true },
						},
					],
				},
				{
					path: '/onboarding',
					name: 'onboarding',
					component: () => import('@/components/school/OnboardingComponent.vue'),
					meta: { title: 'Onboarding', requiresOnobardingAccess: true },
				},
				{
					path: '/dashboard',
					name: 'dashboard',
					component: () => import('@/components/DashboardComponent.vue'),
					meta: { title: 'Dashboard', requiresDashboardAccess: true },
				},

				{
					path: '/staffroom',
					name: 'staffroom',
					// Don't put requiresStaffroomAccess: true in meta here, because there are some children that are public, and we don't want to block access to those
					// instead, we'll put requiresStaffroomAccess: true in the children that need it, which should be most
					children: [
						{
							path: 'restorative-classroom-playbook',
							name: 'restorative-classroom-playbook',
							component: RestorativeClassroomPlaybookComponent,
							meta: { title: 'Restorative Classroom Playbook', requiresStaffroomAccess: true },
							beforeEnter: () => {
								const user = useUser();
								if (!user.canAccessRcp) {
									return { name: 'home' };
								}
							},
						},
						{
							path: 'live-masterclasses',
							component: RouterView,
							children: [
								{
									path: '',
									name: 'live-masterclasses',
									component: OnlineWorkshopsComponent,
									meta: { title: 'Live Masterclasses with Adam Voigt', requiresStaffroomAccess: true },
								},
								{
									path: 'the-better-boys-blueprint',
									component: RouterView,
									meta: { requiresStaffroomAccess: true },
									children: [
										{
											path: '',
											name: 'the-better-boys-blueprint',
											component: MasterclassTheBetterBoysBlueprintComponent,
											meta: { title: 'The Better Boys Blueprint' },
										},
										{
											path: 'resources',
											name: 'the-better-boys-blueprint-resources',
											component: () => import('@/components/MasterclassTheBetterBoysBlueprintResourcesComponent.vue'),
											meta: { title: 'Resources | The Better Boys Blueprint' },
										},
									],
								},
								{
									path: 'the-shame-responsive-school',
									component: RouterView,
									children: [
										{
											path: '',
											redirect: {
												name: 'the-shame-responsive-school-resources',
											},
										},
										{
											path: 'resources',
											name: 'the-shame-responsive-school-resources',
											component: () => import('@/components/MasterclassTheShameResponsiveSchoolResourcesComponent.vue'),
											meta: { title: 'Resources | The Shame Responsive School', isPublic: true },
										},
									],
								},
								{
									path: 'the-ai-ready-student',
									component: RouterView,
									children: [
										{
											path: '',
											name: 'the-ai-ready-student',
											component: () => import('@/components/MasterclassTheAIReadyStudentComponent.vue'),
											meta: { title: 'The AI-Ready Student' },
										},
										{
											path: 'resources',
											name: 'the-ai-ready-student-resources',
											component: () => import('@/components/MasterclassTheAIReadyStudentResourcesComponent.vue'),
											meta: { title: 'Resources | The AI-Ready Student', isPublic: true },
										},
									],
								},
								{
									path: 'the-teacher-retention-solution',
									component: RouterView,
									children: [
										{
											path: '',
											redirect: {
												name: 'the-teacher-retention-solution-resources',
											},
										},
										{
											path: 'resources',
											name: 'the-teacher-retention-solution-resources',
											component: () => import('@/components/MasterclassTheTeacherRetentionSolutionResourcesComponent.vue'),
											meta: { title: 'Resources | The Teacher Retention Solution', isPublic: true },
										},
									],
								},
								{
									path: 'the-bullying-myth',
									component: RouterView,
									children: [
										{
											path: '',
											redirect: {
												name: 'the-bullying-myth-resources',
											},
										},
										{
											path: 'resources',
											name: 'the-bullying-myth-resources',
											component: () => import('@/components/MasterclassTheBullyingMythResourcesComponent.vue'),
											meta: { title: 'Resources | The Bullying Myth', isPublic: true },
										},
									],
								},
								{
									path: 'suspend-smarter',
									component: RouterView,
									children: [
										{
											path: '',
											redirect: {
												name: 'suspend-smarter-resources',
											},
										},
										{
											path: 'resources',
											name: 'suspend-smarter-resources',
											component: () => import('@/components/MasterclassSuspendSmarterResourcesComponent.vue'),
											meta: { title: 'Resources | Suspend Smarter', isPublic: true },
										},
									],
								},
							],
						},
						{
							// Legacy 2026 info-only page. Kept reachable but not linked in the nav.
							path: 'virtual-partnership-accelerator-2026',
							name: 'virtual-partnership-accelerator-2026',
							component: () => import('@/components/VirtualPartnershipAccelerator2026Component.vue'),
							meta: { title: 'Virtual Partnership Accelerator 2026', requiresStaffroomAccess: true },
						},
						// The 2027 VPA page is unguarded on purpose: anyone with the link can
						// open it, whatever their role. That is about the link working when it
						// is passed on — by an EF, a principal, or a champion forwarding it to
						// their staff — rather than bouncing to home. Booking is a different
						// question and is guarded on the server, which scopes the write to the
						// caller's own school and refuses a second one.
						{
							// One page for every partnership year, because there is one
							// list of sessions now. The copy still differs by year — the
							// component branches on it — but the booking does not, so the
							// year no longer belongs in the URL.
							path: 'virtual-partnership-accelerator',
							name: 'virtual-partnership-accelerator',
							component: () => import('@/components/VirtualPartnershipAccelerator2027Component.vue'),
							meta: { title: 'Virtual Partnership Accelerator 2027' },
						},
						// The general one, and the reason the year 3 route carries
						// `-yr3`: this is what the plain path was left unclaimed for.
						// **At the top of the staffroom nav, and the only one of the
						// pair that is in it.** It speaks to every year of a
						// partnership and links a year 3 school on to their own
						// session, where the year 3 page does not work in reverse, so
						// it is the safe one to put in front of whoever is looking.
						// `/admin` lists both, which is how Ops and EFs reach the
						// other one.
						{
							path: 'whats-new-in-partnership',
							name: 'whats-new-in-partnership',
							component: () => import('@/components/WhatsNewInPartnershipAllComponent.vue'),
							meta: { title: "What's New in Partnership" },
						},
						// Unguarded for the same reason the VPA page above is. This one
						// is for partner schools in year 3 and beyond and is the one of
						// the pair that stays out of the nav — Ops and EFs send the link
						// to the schools it is meant for, and the all-partners page above
						// links across to it. A year gate is available (`school.year` is
						// computed by the backend), and is deliberately not used: the
						// link is forwarded on from the person who receives it, and the
						// cost of bouncing a champion to the home page with no
						// explanation is higher than a year 2 school reading ahead.
						// `-yr3` is in the path because the all-partners page holds the
						// bare `whats-new-in-partnership`, and the two have to be
						// tellable apart by whoever is pasting a link into an email.
						// That path was left unclaimed for it rather than redirected
						// here — this page had never been sent to anybody, so there was
						// no old link to keep alive, and a redirect would have taken
						// the better name.
						{
							path: 'whats-new-in-partnership-yr3',
							name: 'whats-new-in-partnership-yr3',
							component: () => import('@/components/WhatsNewInPartnershipComponent.vue'),
							meta: { title: "What's New in Partnership" },
						},
						{
							// The year-specific URLs were sent to schools in email and
							// are forwarded between staff, so they keep working rather
							// than landing somebody on the 404 page months later.
							path: 'virtual-partnership-accelerator-2027-y1',
							redirect: { name: 'virtual-partnership-accelerator' },
						},
						{
							path: 'virtual-partnership-accelerator-2027-y2-3',
							redirect: { name: 'virtual-partnership-accelerator' },
						},
						{
							// One school's operational data — the dates, hours and
							// units Ops and EFs maintain, which until now could only
							// be changed in wp-admin.
							//
							// Under the staffroom and with no id in the path, because
							// the staffroom is already "the school you are in": the
							// id-keyed route this replaces sat under /schools, so an
							// EF with one school in mind had to go to a list of all of
							// them and pick it out.
							//
							// `requiresEfOrMore` is the gate the schools list has; the
							// real check is SchoolController, which admits the same
							// three roles and then scopes an EF to their own schools.
							// A champion is deliberately out — the dates they need are
							// on the Partnership page.
							path: 'school-details',
							name: 'schoolDetails',
							component: () => import('@/components/school/SchoolDetailComponent.vue'),
							meta: { title: 'School Details', requiresEfOrMore: true },
						},
						{
							path: 'partnership',
							name: 'partnership',
							component: () => import('@/components/PartnershipComponent.vue'),
							meta: { title: 'Partnership', requiresPartnershipAccess: true },
							beforeEnter: () => {
								const user = useUser();
								if (!user.isAdminEf && !user.school?.flags?.partnership_enabled) {
									return { name: 'home' };
								}
							},
						},
						{
							path: 'monthly-message',
							name: 'monthly-message',
							component: () => import('@/components/MonthlyMessageComponent.vue'),
							meta: { title: 'Monthly Message', requiresStaffroomChampionAccess: true },
						},
						{
							path: 'staff-management',
							name: 'staff-management',
							component: () => import('@/components/StaffManagementComponent.vue'),
							meta: { title: 'Staff Management', requiresStaffroomChampionAccess: true },
						},
						{
							path: 'crisis',
							name: 'crisis',
							component: () => import('@/components/CrisisComponent.vue'),
							meta: { title: 'Crisis', requiresDashboardAccess: true },
						},
					],
				},
				{
					path: '/resources',
					component: RouterView,
					children: [
						{
							path: '',
							name: 'resourceList',
							component: ResourcesListComponent,
							meta: { title: 'Resources' },
							beforeEnter: (_to, from) => {
								// If they are coming from a single resource we don't want to reset the resource filter
								if (from.name !== 'resourceSingle') {
									const resourceStore = useResourceStore();
									resourceStore.resetFilters();
								}
							},
						},
						{
							path: ':slug',
							name: 'resourceSingle',
							component: ResourceSingleComponent,
							meta: { isPublic: true },
						},
					],
				},
				{
					path: '/account',
					name: 'account',
					component: AccountComponent,
					meta: { title: 'Account' },
				},
				{
					path: '/feedback',
					name: 'feedback',
					component: () => import('@/components/FeedbackComponent.vue'),
					meta: { title: 'Feedback' },
				},
				{
					path: '/principal-message/:slug?',
					name: 'principalMessage',
					component: PrincipalChampionMessageComponent,
					props: {
						type: 'principal',
					},
					meta: { title: 'Principal Message', hideNav: true, isPublic: true },
				},
				{
					path: '/champion-message/:slug?',
					name: 'championMessage',
					component: PrincipalChampionMessageComponent,
					props: {
						type: 'champion',
					},
					meta: { title: 'Champion Message', hideNav: true, isPublic: true },
				},
				{
					path: '/school-map',
					name: 'schoolMap',
					component: () => import('@/components/SchoolMapComponent.vue'),
					meta: { title: 'School Map', hideNav: true, isPublic: true },
				},
				{
					path: '/purchase',
					component: RouterView,
					meta: { isPublic: true },
					children: [
						{
							path: 'book',
							name: 'book-purchase',
							component: () => import('@/components/purchase/BookPurchaseComponent.vue'),
							meta: { title: 'Book Purchase' },
						},
						{
							path: 'complete',
							name: 'purchase-complete',
							component: () => import('@/components/purchase/PaidComponent.vue'),
							meta: { title: 'Thanks for your purchase' },
						},
					],
				},
				{
					path: '/events',
					component: RouterView,
					meta: { isPublic: true },
					children: [
						{
							// The page for an event that has no page of its own, which
							// until now meant every free event: the purchase page below
							// refuses a free event, and the only other way in was a
							// bespoke component built for that one session. Public like
							// its siblings — the registration block offers a logged-out
							// visitor the login link and brings them back here.
							path: ':slug',
							name: 'event-page',
							component: () => import('@/components/events/EventPageComponent.vue'),
							meta: { title: 'Event' },
						},
						{
							path: ':slug/register',
							name: 'event-register',
							component: () => import('@/components/events/EventPurchaseComponent.vue'),
							meta: { title: 'Event Registration' },
						},
						{
							// Public, like its siblings. It is the link in the
							// confirmation, both reminders, the day-of email and the
							// calendar invitation — and a paid event's attendees are
							// typed names with no portal account at all, so a login
							// wall here would lock out the people most likely to use
							// it, two minutes before the session starts. The Zoom URL
							// it resolves to is in their inbox already.
							path: ':slug/join',
							name: 'event-join',
							component: () => import('@/components/events/EventJoinComponent.vue'),
							meta: { title: 'Joining…' },
						},
						{
							// The shape these URLs had until the `:slug/join` sibling
							// made a verb-first path untenable. They are pasted into
							// pages on realschools.com.au and sent in email, neither of
							// which can be edited retrospectively, so they keep working.
							path: 'register/:slug',
							redirect: (to) => ({ name: 'event-register', params: { slug: to.params.slug } }),
						},
					],
				},
				{
					path: '/oauth/authorize',
					name: 'oauth-authorize',
					component: () => import('@/components/auth/OAuthAuthorizeComponent.vue'),
					meta: { title: 'Authorise Access', isPublic: true },
				},
				{
					path: '/bot/:agentId?',
					name: 'bot',
					component: () => import('@/components/RisposeComponent.vue'),
					meta: { title: 'Bot', isPublic: true },
				},
				{
					path: '/redirector',
					name: 'redirector',
					component: () => import('@/components/RedirectorComponent.vue'),
					meta: { title: 'Redirector', isPublic: true },
				},
				{
					path: '/no-access',
					name: 'no-access',
					component: () => import('@/components/NoAccessComponent.vue'),
					meta: { title: 'No Access' },
				},
				{
					path: '/404',
					name: '404',
					component: () => import('@/components/Four04Component.vue'),
					meta: { title: "You're lost!" },
				},
				{
					path: '/login',
					redirect: () => {
						return { name: 'login' };
					},
				},
				{
					path: '/:pathMatch(.*)',
					redirect: (to) => {
						// TODO
						// Detail the difference between these redirects and the ones in the router beforeEach below
						// Then possibly combine them
						const vueRedirects: Record<string, string> = {
							'/book': '/staffroom/book',
							'/home': '/dashboard',
							'/home/crisis': '/staffroom/crisis',
							'/pl-day-three-survey-analyser': '/ef/survey-analyser',
							'/ef-resources/pl-day-three-survey-analyser': '/ef/survey-analyser',
							'/staffroom/blue-black-book': '/staffroom/partnership-handbooks',
							'/training': '/learning',
							'/student-behaviour-genius': '/staffroom/student-behaviour-genius',
							'/staffroom/newsletter': '/staffroom/newsletter-generator',
							'/coach': '/staffroom/restorative-lesson-coach',
							'/restorative-lesson-coach': '/staffroom/restorative-lesson-coach',
							'/messaging-genius': '/staffroom/messaging-genius',
							'/staffroom/student-behaviour-phd': '/learning',
							'/student-behaviour-phd': '/learning',
							'/upcoming': '/learning',
							'/staffroom/student-behaviour-genius': '/resources/student-behaviour-genius',
							'/staffroom/restorative-lesson-coach': '/resources/restorative-lesson-coach',
							'/staffroom/messaging-genius': '/resources/parent-teacher-messaging-genius',
							'/staffroom/newsletter-generator': '/resources/school-newsletter-generator',
							'/staffroom/book': '/resources/restoring-teaching-book',
							'/staffroom/instruction-and-relationships': '/resources/instruction-and-relationships',
							'/staffroom/partnership-handbooks': '/resources/partnership-handbooks',
							'/ef-resources/realbot-online-curriculum': '/resources/learning-online-curriculum',
							'/ef-resources/realbot-phd': '/resources/learning-student-behaviour-phd',
							'/staffroom/feedback': '/feedback',
							'/staffroom/about': '/account',
							'/staffroom/online-workshops': '/staffroom/live-masterclasses',
							'/staffroom/the-better-boys-blueprint': '/staffroom/live-masterclasses/the-better-boys-blueprint',
							'/staffroom/the-better-boys-blueprint/resources': '/staffroom/live-masterclasses/the-better-boys-blueprint/resources',
							'/staffroom/the-shame-responsive-school': '/staffroom/live-masterclasses/the-shame-responsive-school',
							'/staffroom/the-shame-responsive-school/resources': '/staffroom/live-masterclasses/the-shame-responsive-school/resources',
							'/staffroom/charm-offensive-generator': '/resources/charm-offensive-generator',
							// Events moved out of Admin when operations gained read access to it,
							// and Activity followed it under the prefix its menu group already had.
							'/admin/events': '/ops/events',
							'/activity': '/ops/activity',
							// Followed them when operations gained access to the advisor.
							'/admin/policy-advisor': '/ops/policy-advisor',
							// The EF group was /ef-resources, which named two of its four
							// pages wrongly, and Event Bookings sat outside it entirely.
							'/ef-resources': '/ef',
							'/ef-resources/survey-creator': '/ef/survey-creator',
							'/ef-resources/survey-analyser': '/ef/survey-analyser',
							'/event-bookings': '/ef/event-bookings',
						};

						const path = to.path;

						if (path in vueRedirects) {
							const newPath = vueRedirects[path];
							return { path: newPath };
						}

						return { name: '404' };
					},
				},
			],
		});

		// Route components are lazy-loaded, so a tab left open across a deploy can fail to fetch
		// the new chunk. Reload once to pick up the current assets — see staleAssetService for
		// the loop guard and the full list of messages this covers.
		router.onError((error, to) => {
			if (!isStaleAssetError(error)) {
				return;
			}

			reloadForStaleAsset(to?.fullPath);
		});

		// Update title tag in HTML
		const sitename = 'Partner Portal | Real Schools';
		router.afterEach((to) => {
			document.title = to.meta.title ? `${to.meta.title} | ${sitename}` : sitename;

			trackPageView(String(to.name ?? ''), to.path);
		});

		// Guards and redirects
		router.beforeEach(async (to) => {
			if (to.name === 'no-access') {
				return;
			}

			const hrefRedirects = [
				{
					pattern: /^\/resources\/rp2-0-and-doewa-teach-for-impact-whitepaper-2025\/?$/,
					redirect: 'https://backend.partner-portal.realschools.com.au/wp-content/uploads/2025/04/RP2.0-and-DOEWA-Teach-for-Impact.pdf',
				},
				{
					pattern: /^\/resources\/when-things-go-wrong-at-school\/?$/,
					redirect: '/resources/how-can-my-child-learn-from-their-mistakes',
				},
				{
					pattern: /^\/resources\/individual-behaviour-plan-tutorial\/?$/,
					redirect: '/resources/individual-behaviour-plan',
				},
				{
					pattern: /^\/resources\/individual-behaviour-planning\/?$/,
					redirect: '/resources/individual-behaviour-plan',
				},
			];

			const matchedRedirect = hrefRedirects.find((redirect) => redirect.pattern.test(to.path));
			if (matchedRedirect) {
				window.location.href = matchedRedirect.redirect;
				return;
			}

			const user = useUser();

			if (to.matched.some((record) => record.meta.forceLogout) && user.isLoggedIn) {
				await user.logout(null);
			}

			// Have the login checker at the top so we can always capture the URL
			// that the wanted to go to, so once they are logged in we can try and
			// send them there, and if they can't access that URL then that will
			// be captured below
			if (!user.isLoggedIn && !to.meta.isPublic) {
				return {
					name: 'login',
					query: {
						redirect: to.fullPath,
					},
				};
			}

			if (to.meta.requiresPublic && user.isLoggedIn) {
				return {
					name: 'home',
				};
			}

			/**
			 * If a user is logged in, presumably their schools have been loaded, so if they don't have any, it means they have
			 * probably been removed from a school, but their user account still exists. So they shouldn't have access to anything.
			 *
			 * Unless they are an EF which means they might not have access to a school but still can see basic portal stuff.
			 *
			 * This didn't used to be an issue, but now that we sync users from Hubspot automatically, we can have users that
			 * are logged in but don't have access to any schools, so we need to handle that
			 * case here.
			 *
			 * But if they can access a partnership cluster then don't block them.
			 *
			 * Public routes are exempt, and that is the same `isPublic` flag the login check above
			 * uses: a public route is a page we're happy for a complete stranger to see, so someone
			 * who happens to be logged in may see it too. Without this, sending a school a resource
			 * link worked for anyone who opened it logged out and failed for exactly the people who
			 * had an account — logging in showed them less than logging out did.
			 */
			if (user.isLoggedIn && !user.canAccessPortal && !to.meta.isPublic) {
				return {
					name: 'no-access',
				};
			}

			// Admin guard
			if (to.matched.some((record) => record.meta.requiresAdmin) && !user.isAdmin) {
				return {
					name: 'home',
				};
			}

			// Ops guard. Central RS staff, which is admins and operations — narrower
			// than EF, who are not operations. A page behind this may still be
			// read-only for some of them; the backend decides that per action.
			if (to.matched.some((record) => record.meta.requiresOps) && !user.canAccessOps) {
				return {
					name: 'home',
				};
			}

			// If going to school but not one set, go to all schools
			if (to.matched.some((record) => record.meta.requiresSchool) && !user.school) {
				return {
					name: 'schoolList',
				};
			}

			if (to.matched.some((record) => record.meta.requiresChampionOrMore) && !user.isAdminEfChampion) {
				return {
					name: 'home',
				};
			}

			if (to.matched.some((record) => record.meta.requiresEfOrMore) && !user.isAdminEf) {
				return {
					name: 'home',
				};
			}

			if (to.matched.some((record) => record.meta.requiresDashboardAccess) && !user.canAccessDashboard) {
				return {
					name: 'home',
				};
			}

			if (to.matched.some((record) => record.meta.requiresOnobardingAccess) && !user.canAccessOnboarding) {
				return {
					name: 'home',
				};
			}

			if (to.matched.some((record) => record.meta.requiresLearningAccess) && !user.canAccessLearning) {
				return {
					name: 'home',
				};
			}

			if (to.matched.some((record) => record.meta.requiresPartnershipAccess) && !user.canAccessPartnership) {
				return {
					name: 'home',
				};
			}

			if (to.matched.some((record) => record.meta.requiresStaffroomAccess) && !user.canAccessStaffroom) {
				return {
					name: 'home',
				};
			}

			if (to.matched.some((record) => record.meta.requiresStaffroomChampionAccess) && !user.canAccessStaffroomChampion) {
				return {
					name: 'home',
				};
			}
		});

		// Fix old resource URLs which used to be hashed based routing. So redirect
		// /partner-portal/#/resources/some-slug to /partner-portal/resources/some-slug
		// This needs to be anove the main guard below
		router.beforeEach((to) => {
			const pathToWatch = '/#/resources/';
			if (to.fullPath.indexOf(pathToWatch) === 0) {
				const slug = to.fullPath.replace(pathToWatch, '');

				return {
					name: 'resourceSingle',
					params: {
						slug,
					},
				};
			}
		});
	}

	return router;
};

// https://router.vuejs.org/guide/advanced/meta#TypeScript
// Note that this does not FORCE the routes to only use this type
import 'vue-router';
declare module 'vue-router' {
	interface RouteMeta {
		requiresPublic?: boolean;
		isPublic?: boolean;
		// If true, will log the user out before showing the page
		forceLogout?: boolean;

		title?: string;
		requiresSchool?: boolean;
		hideNav?: boolean;

		requiresOnobardingAccess?: boolean;
		requiresDashboardAccess?: boolean;
		requiresLearningAccess?: boolean;
		requiresPartnershipAccess?: boolean;
		requiresStaffroomAccess?: boolean;
		requiresStaffroomChampionAccess?: boolean;

		requiresAdmin?: boolean;
		requiresOps?: boolean;
		requiresEfOrMore?: boolean;
		requiresChampionOrMore?: boolean;
	}
}
